import React from 'react';
import { StyleSheet, Text } from 'react-native';
import { TYPOGRAPHY } from '@/styles';

interface Article {
  bodyContent: React.ReactNode;
  imageURL: string;
  name: string;
  titleContent: React.ReactNode;
}

const CURRENT_YEAR = new Date().getFullYear();

const styles = StyleSheet.create({
  articleParagraph: {
    marginBottom: 8,
  },
  articleSubtitle: {
    marginBottom: 16,
  },
  articleTitle: {
    marginVertical: 8,
  },
});

export const ARTICLE_MAP = {
  deliciousPlantBasedBurgers: {
    bodyContent: (
      <>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          As the popularity of plant-based diets continues to soar, the demand
          for mouthwatering and satisfying plant-based alternatives has never
          been higher. One culinary delight that has undergone a remarkable
          transformation in the world of plant-based eating is the classic
          burger. Gone are the days when veggie burgers were relegated to the
          sidelines; today, plant-based burgers are taking center stage,
          offering flavors and textures that rival their meat counterparts. In
          this article, we explore nine delectable plant-based burgers that are
          sure to tantalize your taste buds and leave you craving more.
        </Text>
        <Text
          style={[TYPOGRAPHY.medium, TYPOGRAPHY.pBold, styles.articleParagraph]}
        >
          1. Beyond Burger: The Pioneer
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          One of the pioneers in the plant-based burger revolution, the Beyond
          Burger has earned its place at the top. Known for its juicy, texture
          texture and savory taste, this burger has become a staple in many
          households and restaurants alike. Made from pea protein, it's a
          satisfying option for both vegetarians and omnivores.
        </Text>
        <Text
          style={[TYPOGRAPHY.medium, TYPOGRAPHY.pBold, styles.articleParagraph]}
        >
          2. Impossible Burger: Breaking Boundaries
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          The Impossible Burger has made waves for its ability to replicate
          taste and texture of traditional beef burgers, thanks to its
          innovative use of heme, a molecule found in plants and meat. This
          burger is not just for vegetarians; it's a game-changer for anyone
          looking to reduce their environmental impact without compromising on
          flavor.
        </Text>
        <Text
          style={[TYPOGRAPHY.medium, TYPOGRAPHY.pBold, styles.articleParagraph]}
        >
          3. Black Bean Burger: Hearty and Wholesome
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          For a more traditional approach, the black bean burger is a timeless
          favorite. Packed with protein, fiber, and a medley of spices, this
          burger is not only delicious but also incredibly nutritious. Top it
          with avocado, salsa, and a dollop of vegan mayo for a burst of
          freshness.
        </Text>
        <Text
          style={[TYPOGRAPHY.medium, TYPOGRAPHY.pBold, styles.articleParagraph]}
        >
          4. Chickpea and Quinoa Burger: The Protein Powerhouse
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          Combining the goodness of chickpeas and quinoa, this burger is a
          protein-packed delight. The nutty flavor of quinoa complements the
          earthiness of chickpeas, creating a satisfying and wholesome patty.
          Add your favorite toppings and enjoy a guilt-free indulgence.
        </Text>
        <Text
          style={[TYPOGRAPHY.medium, TYPOGRAPHY.pBold, styles.articleParagraph]}
        >
          5. Portobello Mushroom Burger: Nature's Bounty
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          For a burger that celebrates the essence of whole, natural foods, the
          Portobello mushroom burger is a delightful choice. Grilled to
          perfection, these meaty mushrooms offer a rich and savory experience.
          Layer with caramelized onions, spinach, and a drizzle of glaze for a
          gourmet twist.
        </Text>
      </>
    ),
    imageURL:
      'https://s3.amazonaws.com/media-dev.usepepper.com/vegan_burger.png',
    name: '5 Delicious Plant-Based Burgers',
    titleContent: (
      <>
        <Text style={[TYPOGRAPHY.h1, styles.articleTitle]}>
          5 Delicious Plant-Based Burgers
        </Text>
        <Text style={[TYPOGRAPHY.large, styles.articleSubtitle]}>
          Meatless burgers that are hearty and full of flavor.
        </Text>
      </>
    ),
  } as Article,
  elevatedDiningExperience: {
    bodyContent: (
      <>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          Offering a multitude of beverages is paramount to ensuring a memorable
          guest experience in dining establishments.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          A robust drink menu can significantly{' '}
          <Text style={TYPOGRAPHY.pBold}>
            increase your share of the wallet
          </Text>{' '}
          by catering to an array of customer preferences, including those who,
          for various reasons, may opt out of an alcoholic beverage but still
          seek an extraordinary drink to accompany their meal.{' '}
          <Text style={TYPOGRAPHY.pBold}>
            Mocktails have surged in popularity
          </Text>
          , capturing the interest of guests who desire the sense of a special
          evening without the alcohol content. These non-alcoholic wonders allow
          restaurants to craft unique beverage experiences that are both
          inclusive and sophisticated, elevating the dining atmosphere and
          broadening their clientele. It's about more than quenching thirst;
          it's about crafting moments that patrons savour just as much as the
          food on their plates.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          The significance of a diversified beverage menu extends beyond just
          variety; it touches on{' '}
          <Text style={TYPOGRAPHY.pBold}>
            the evolving trend among consumers to make healthier lifestyle
            choices
          </Text>
          . Beverages such as mocktails often feature fresh ingredients, fewer
          calories, and an alignment with the cultural shift towards wellness.
          In an age where diners' palates are becoming more exploratory, an
          assortment of drinks - ranging from globally-inspired concoctions to
          locally-sourced herbal infusions - showcases the restaurant's
          commitment to delivering novel and delightful experiences. Such a
          kaleidoscope of options not only satisfies the curious and
          health-conscious but{' '}
          <Text style={TYPOGRAPHY.pBold}>
            can also forge lasting impressions
          </Text>
          , turning a simple meal into an event that is talked about, shared on
          social media, and reflected positively in consumer feedback. As market
          research and social trends constantly reveal, garnishing the dining
          experience with a splash of beverage diversity is a choice that pays
          dividends in customer satisfaction and loyalty.
        </Text>
      </>
    ),
    imageURL:
      'https://pepper-restaurant-app-production.s3.amazonaws.com/6d1bb1a5-cce0-416b-9430-10600acb0a3b.jpg',
    name: 'Elevated Dining Experience',
    titleContent: (
      <>
        <Text style={[TYPOGRAPHY.h1, styles.articleTitle]}>
          Elevated Dining Experiences
        </Text>
        <Text style={[TYPOGRAPHY.large, styles.articleSubtitle]}>
          The Impact of Diversified Beverage Menus
        </Text>
      </>
    ),
  } as Article,
  feastingWithFathers: {
    bodyContent: (
      <>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          With Father's Day on the horizon, it's time to seize the opportunity
          to create an extraordinary experience that will make dads feel truly
          special.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          By curating a menu that features dad favourites such as succulent
          steak and luxurious lobster, you can express their appreciation for
          all the fathers out there.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          Perhaps even go beyond the food, consider hosting an outdoor feast,
          screening a classic film outdoors, or even oftering surprise upgrades
          to enhance their dining experience.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          <Text style={TYPOGRAPHY.pBold}>AAA Striploin Frozen</Text> The New
          York strip is a staple cut that does equally well being cooked over a
          searing hot grill, in a pan or diced into a stir fry. We hand select
          only the highest quality Canadian AAA grade striploins from trusted
          producers and farms across Canada. Each striploin is wet aged for a
          minimum of 30 days to bring out the natural flavour and tenderness of
          the beef. After aging, each steak is hand cut to order and packaged to
          ensure peak freshness and flavour.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          <Text style={TYPOGRAPHY.pBold}>AAA Ribeye Frozen</Text> Also known as
          the Delmonico steak or The King of Steaks known for its rich beefy
          flavour and tenderness. Our ribeye steaks are cut from Canadian AAA
          grade beef ribs, each rib is wet aged for a minimum of 30 days to
          enhance the flavour and bring out a superior tenderness making for a
          truly great cut of beef. The ribs are then removed to reveal the
          ribeye and hand cut each steak which is then frozen to ensure peak
          freshness.
        </Text>
      </>
    ),
    imageURL:
      'https://pepper-restaurant-app-production.s3.amazonaws.com/f73f5e52-76d2-4fb0-9709-36e9eff4ee06.jpg',
    name: 'Feasting with Fathers',
    titleContent: (
      <>
        <Text style={TYPOGRAPHY.large}>Feasting with Fathers:</Text>
        <Text style={[TYPOGRAPHY.h1, styles.articleTitle]}>
          Honouring DADS with a Gourmet Menu this Father's Day
        </Text>
      </>
    ),
  } as Article,
  foodTrends: {
    bodyContent: (
      <>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          As a wholesale food distributor, we’re keenly aware of the evolving
          landscape in the restaurant industry for {CURRENT_YEAR}. Here’s an
          overview of the top food trends that restaurants should be aware of:
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          <Text style={TYPOGRAPHY.pBold}>1. Sustainable Sourcing:</Text>{' '}
          Environmental consciousness is at an all-time high, and restaurants
          are increasingly sourcing ingredients from sustainable, ethical
          producers. This trend extends to both plant-based options and
          ethically sourced meats and seafood.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          <Text style={TYPOGRAPHY.pBold}>2. Functional Foods:</Text> Healthier
          menu options are in demand, with a focus on nutrient-rich, functional
          foods. Think dishes incorporating superfoods, fermented items, and
          ingredients with health-boosting properties.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          <Text style={TYPOGRAPHY.pBold}>
            3. Global Flavors and Fusion Cuisine:
          </Text>{' '}
          Diners are craving new, exotic flavors from around the globe. African,
          South American, and Southeast Asian cuisines are particularly popular,
          often combined in innovative fusion dishes.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          <Text style={TYPOGRAPHY.pBold}>4. Plant-Based Menus:</Text> The
          plant-based movement continues to grow, with an emphasis on
          innovative, satisfying options that go beyond the traditional veggie
          burger. Expect to see more plant-based meats, dairy-free cheeses, and
          creative vegetarian and vegan dishes.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          <Text style={TYPOGRAPHY.pBold}>5. Comfort Food with a Twist:</Text>{' '}
          Comfort food remains popular, but with a modern twist. Classic dishes
          are being revamped with unique ingredients and presentation styles to
          offer both familiarity and novelty.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          <Text style={TYPOGRAPHY.pBold}>6. Low- and No-Alcohol Options:</Text>{' '}
          Reflecting a broader health trend, there’s an increasing demand for
          sophisticated low- and no-alcohol beverages, from mocktails to
          non-alcoholic spirits and beers.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          <Text style={TYPOGRAPHY.pBold}>
            7. Tech-Driven Dining Experience:
          </Text>{' '}
          Technology is transforming the dining experience. From QR code menus
          to AI-driven personalization and online ordering systems, tech is
          becoming an integral part of the restaurant industry.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          These trends are shaping the future of dining in {CURRENT_YEAR},
          offering exciting opportunities for restaurants to innovate and
          attract customers.
        </Text>
      </>
    ),
    imageURL:
      'https://s3.amazonaws.com/media-dev.usepepper.com/istock-1155240408.jpg',
    name: `Food Trends of ${CURRENT_YEAR}`,
    titleContent: (
      <>
        <Text style={[TYPOGRAPHY.h1, styles.articleTitle]}>
          Food Trends of {CURRENT_YEAR}
        </Text>
        <Text style={[TYPOGRAPHY.large, styles.articleSubtitle]}>
          Top Food and Beverage Industry Predictions
        </Text>
      </>
    ),
  } as Article,
  lobsterKingOfSeafood: {
    bodyContent: (
      <>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          Lobster has long been considered a luxury item in the culinary world,
          adding a touch of opulence to any restaurant menu. The presence of
          lobster dishes can elevate a dining establishments status, suggesting
          a commitment to offering high-quality, gourmet experiences. Its sweet,
          succulent meat is prized for its delicate flavour and tender texture,
          making it a favourite among seafood aficionados. Moreover, lobster
          provides significant value through its versatility; it can be prepared
          in numerous ways, accommodating a range of palates and menu styles.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          For many, lobster is an indulgence typically reserved for special
          occasions, and an item they are more likely to order in a restaurant
          than prepare at home. The expertise required in cooking lobster to
          perfection, not to mention the process of breaking down and serving
          this crustacean, is a task most diners prefer to leave to professional
          chefs. Alongside, the ambience of a restaurant provides a fitting
          backdrop to enjoy such a luxurious treat, enhancing the overall dining
          experience.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          Chefs looking to innovate their menus with lobster can draw
          inspiration from the following menu ideas:
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          <Text style={TYPOGRAPHY.pBold}>Lobster Thermidor:</Text> A classic
          French dish where succulent lobster meat is mixed with a creamy sauce,
          gruyere cheese, and a splash of brandy, topped with breadcrumbs, then
          baked until golden.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          <Text style={TYPOGRAPHY.pBold}>
            Grilled Lobster Tails with Herb Butter:
          </Text>{' '}
          Perfect for summer menus, offering a lighter option, these lobster
          tails are basted with a flavourful herb-infused butter, grilled to
          perfection, and served with lemon wedges and a selection of dips.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          <Text style={TYPOGRAPHY.pBold}>Lobster Risotto:</Text> A rich and
          creamy risotto, incorporating chunks of lobster meat, complemented
          with a touch of saffron and a sprinkle of parmesan cheese, providing
          an elegant entree or main course option.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          <Text style={TYPOGRAPHY.pBold}>Lobster Mac n Cheese:</Text> A
          luxurious take on a comfort food classic, this dish combines rich,
          creamy cheese sauce with hearty elbow macaroni and generous chunks of
          lobster meat. Topped with a crust of panko breadcrumbs and a hint of
          cayenne for warmth, its baked to a bubbly, golden brown to delight the
          taste buds of all ages.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          <Text style={TYPOGRAPHY.pBold}>Down East Lobster Rolls:</Text>{' '}
          Embracing the simplicity of the east coasts traditional fare, these
          rolls feature fresh, chilled lobster meat lightly dressed in
          mayonnaise, served in butter grilled hot dog buns. Accompanied by a
          sprinkle of chives and a dash of lemon, it offers an authentic and
          unadulterated lobster experience.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          Incorporating lobster into a restaurants menu not only adds prestige
          but also caters to a segment of diners looking for a dish that is both
          extravagant and celebratory, reinforcing the restaurants image as a
          destination for special dining experiences.
        </Text>
        <Text style={[TYPOGRAPHY.medium, styles.articleParagraph]}>
          All Grille and Galley lobster are procured from the Atlantic provinces
          of Canada.
        </Text>
      </>
    ),
    imageURL:
      'https://pepper-restaurant-app-production.s3.amazonaws.com/358c6fb0-655c-49f5-9c82-182b03f8295f.jpg',
    name: 'Lobster: The King of Seafood',
    titleContent: (
      <Text style={[TYPOGRAPHY.h1, styles.articleTitle]}>
        Lobster: The King of Seafood
      </Text>
    ),
  } as Article,
};

export type ArticleKey = keyof typeof ARTICLE_MAP;

export const DEFAULT_ARTICLE_KEYS: ArticleKey[] = [
  'deliciousPlantBasedBurgers',
  'foodTrends',
];
